import { z } from "zod"

import { useCompany } from "~/hooks/useCompany"
import { useSafeLocalStorage } from "~/hooks/useSafeLocalStorage"
import { differenceInWeeksUTC, getStartOfCurrentWeekUTC } from "~/utils/datetime"

// numberOfWeeks is null when the user hasn't explicitly set it — resolved at read time
export const storedExportConfigSchema = z.object({
  numberOfWeeks: z.number().min(1).max(520).nullable(),
  numberOfDecimals: z.number().min(0).max(10),
})

export const formExportConfigSchema = z.object({
  numberOfWeeks: z.number().min(1).max(520),
  numberOfDecimals: z.number().min(0).max(10),
})

export type StoredExportConfig = z.infer<typeof storedExportConfigSchema>
export type ExportConfig = z.infer<typeof formExportConfigSchema>

export const DEFAULT_EXPORT_DECIMALS = 2

export function useExportConfig(): [ExportConfig, (config: StoredExportConfig) => void] {
  const { data: company } = useCompany()
  const defaultWeeks = company?.maxForecastDate
    ? Math.max(1, differenceInWeeksUTC(company.maxForecastDate, getStartOfCurrentWeekUTC()))
    : 52

  const [stored, setStored] = useSafeLocalStorage<StoredExportConfig>(
    "forecast-export-config",
    { numberOfWeeks: null, numberOfDecimals: DEFAULT_EXPORT_DECIMALS },
    storedExportConfigSchema,
    true,
  )

  return [{ ...stored, numberOfWeeks: stored.numberOfWeeks ?? defaultWeeks }, setStored]
}
